import { CharacterSheet } from '../types/CharacterSheet';

export interface ProfileData {
    id: string;
    name?: string;
    data: CharacterSheet;
}

export const STORAGE_KEY = 'profiles';
export const ACTIVE_KEY = 'activeProfile';
export const LAST_VISIT_KEY = 'lastVisit';
export const ROOM_PROFILE_KEY = 'roomProfiles';
export const MAX_PROFILES = 1000;

export function getProfiles(): ProfileData[] {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
}

export function saveProfiles(profiles: ProfileData[]) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profiles));
}

export function getActiveProfileId(): string | null {
    return localStorage.getItem(ACTIVE_KEY);
}

export function setActiveProfileId(id: string) {
    localStorage.setItem(ACTIVE_KEY, id);
}

function getRoomProfileMap(): Record<string, string> {
    try {
        const raw = localStorage.getItem(ROOM_PROFILE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return {};
        return Object.entries(parsed).reduce<Record<string, string>>((acc, [roomId, profileId]) => {
            if (typeof roomId === 'string' && typeof profileId === 'string' && roomId && profileId) {
                acc[roomId] = profileId;
            }
            return acc;
        }, {});
    } catch {
        return {};
    }
}

function saveRoomProfileMap(map: Record<string, string>) {
    localStorage.setItem(ROOM_PROFILE_KEY, JSON.stringify(map));
}

export function getRoomProfileId(roomId: string): string | null {
    return getRoomProfileMap()[roomId] ?? null;
}

export function setRoomProfileId(roomId: string, profileId: string) {
    if (!roomId || !profileId) return;
    const map = getRoomProfileMap();
    map[roomId] = profileId;
    saveRoomProfileMap(map);
}

export function getLastVisit(): number {
    return parseInt(localStorage.getItem(LAST_VISIT_KEY) ?? '0', 10);
}

export function setLastVisit(ts: number = Date.now()) {
    localStorage.setItem(LAST_VISIT_KEY, ts.toString());
}

export function addProfile(profile: ProfileData): boolean {
    const profiles = getProfiles();
    if (profiles.length >= MAX_PROFILES) return false;
    profiles.push(profile);
    saveProfiles(profiles);
    setActiveProfileId(profile.id);
    return true;
}

export function updateProfile(id: string, data: CharacterSheet) {
    const profiles = getProfiles();
    const idx = profiles.findIndex(p => p.id === id);
    if (idx !== -1) {
        profiles[idx].data = data;
        profiles[idx].name = data.name ?? '';
        saveProfiles(profiles);
    }
}

export function deleteProfile(id: string) {
    const profiles = getProfiles().filter(p => p.id !== id);
    saveProfiles(profiles);
    if (getActiveProfileId() === id) {
        localStorage.removeItem(ACTIVE_KEY);
    }
    const map = getRoomProfileMap();
    let changed = false;
    Object.entries(map).forEach(([roomId, profileId]) => {
        if (profileId === id) {
            delete map[roomId];
            changed = true;
        }
    });
    if (changed) {
        saveRoomProfileMap(map);
    }
}

export function getProfileById(id: string): ProfileData | undefined {
    return getProfiles().find(p => p.id === id);
}
